import { useState } from 'react'
import { TaskService, type Task, type TaskStep } from '../../services/taskService'
import { ActionItemService } from '../../services/actionItemService'
import { StepsList } from '../ui/StepsList'

/**
 * Steps — the task's own short checklist (owner 2026-09-05: "should just be a
 * short list and it does not necessarily end in Action Items"). The list
 * itself is generic (`ui/StepsList`); this wrapper knows how a task saves,
 * and still lists the older AI breakdowns that became action items so
 * nothing the user already has disappears.
 */
interface Props {
  task: Task
  onChanged: (task: Task) => void
  onBreakdown?: () => Promise<void> | void
  breakdownBusy?: boolean
  heading?: boolean
  openRequest?: number
}

export function TaskSteps({ task, onChanged, onBreakdown, breakdownBusy = false, heading = true, openRequest = 0 }: Props) {
  const svc = new TaskService()
  const [busy, setBusy] = useState(false)
  const steps: TaskStep[] = task.steps || []
  const items = (task.child_action_items || []).filter((a) => a.status !== 'cancelled')
  const open = task.status !== 'completed' && task.status !== 'cancelled'
  const hasAi = Boolean(task.has_ai_breakdown) || steps.some((s) => s.source === 'ai')

  const save = async (next: TaskStep[]) => {
    await svc.updateTask(task.id, { steps: next })
    onChanged(await svc.getTask(task.id))
  }
  const toggleItem = async (id: string | number, completed: boolean) => {
    setBusy(true)
    try {
      await new ActionItemService().updateActionItem(String(id), { status: completed ? 'pending' : 'completed' })
      onChanged(await svc.getTask(task.id))
    } finally { setBusy(false) }
  }

  return (
    <StepsList
      steps={steps}
      onSave={save}
      open={open}
      hasAi={hasAi}
      onBreakdown={onBreakdown}
      breakdownBusy={breakdownBusy}
      heading={heading}
      storeKey={`task.steps.open.${task.id}`}
      openRequest={openRequest}
      testId="task-steps"
      extraCount={items.length}
      extraDone={items.filter((a) => a.status === 'completed').length}
      extra={items.map((a) => (
        <li key={`ai-${a.id}`} className="flex items-start gap-2 rounded-md px-1 py-1 hover:bg-surface-alt">
          <input type="checkbox" checked={a.status === 'completed'} disabled={!open || busy} onChange={() => void toggleItem(a.id, a.status === 'completed')} aria-label={`${a.status === 'completed' ? 'Reopen' : 'Finish'} step: ${a.title}`} className="mt-1 h-4 w-4 accent-brand" />
          <span className={`min-w-0 flex-1 text-sm ${a.status === 'completed' ? 'text-muted line-through decoration-soft' : 'text-primary'}`}>{a.title}</span>
        </li>
      ))}
    />
  )
}

export default TaskSteps
